<template>
  <div class="fp" ref="raiz">
    <button
      type="button"
      class="fp__gatilho"
      :class="{ 'fp__gatilho--filtrando': value.modo !== 'tudo' }"
      @click="toggle"
    >
      <fa-icon icon="calendar-alt" />
      <span class="fp__rotulo">Período:</span>
      {{ resumo }}
      <fa-icon icon="chevron-down" class="fp__chevron" :class="{ 'fp__chevron--aberto': aberto }" />
    </button>

    <div v-show="aberto" class="fp__painel" ref="painel" :style="estiloPainel">
      <div class="fp__acoes">
        <span class="fp__contagem">Intervalo</span>
      </div>

      <ul class="fp__lista">
        <li v-for="opcao in atalhos" :key="opcao.valor">
          <label class="fp__item" :class="{ 'fp__item--marcado': pendente.modo === opcao.valor }">
            <input type="radio" :value="opcao.valor" v-model="pendente.modo" @change="onAtalho" />
            <span class="fp__nome">{{ opcao.rotulo }}</span>
          </label>
        </li>
      </ul>

      <!-- Range específico. Preencher qualquer campo muda o modo para "custom",
           e escolher um atalho acima limpa os dois campos. -->
      <div class="fp__range">
        <div class="fp__range-titulo">Ou um intervalo específico</div>
        <div class="fp__campos">
          <label class="fp__campo">
            <span>De</span>
            <input type="date" v-model="pendente.dataInicio" @change="onData" />
          </label>
          <label class="fp__campo">
            <span>Até</span>
            <input type="date" v-model="pendente.dataFim" @change="onData" />
          </label>
        </div>
        <div v-if="avisoRange" class="fp__aviso">{{ avisoRange }}</div>
      </div>

      <div v-if="temPendencia" class="fp__rodape">
        <span class="fp__pendente">{{ resumoPendente }}</span>
        <CButton color="primary" size="sm" :disabled="!podeAplicar" @click="aplicar">
          APLICAR
        </CButton>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Filtro de período: atalhos (hoje / 3 dias / 7 dias / tudo) ou um intervalo
 * específico de datas.
 *
 * Segue o mesmo contrato de interação dos filtros de Tipo e Status: a escolha
 * fica PENDENTE e só é aplicada (emitindo `input`) no APLICAR ou ao dispensar o
 * painel (clique fora / no gatilho). Esc fecha descartando.
 *
 * v-model: { modo: 'hoje'|'3dias'|'7dias'|'tudo'|'custom', dataInicio, dataFim }
 *
 * Usa <input type="date"> nativo de propósito: o calendário é desenhado pelo
 * navegador, fora do DOM, então não dispara o clique-fora que fecharia o painel
 * (o que aconteceria com um datepicker em JS montado no body).
 */
import dropdownPosicao from './dropdownPosicao';

const ATALHOS = [
  { valor: 'hoje', rotulo: 'Hoje' },
  { valor: '3dias', rotulo: 'Últimos 3 dias' },
  { valor: '7dias', rotulo: 'Últimos 7 dias' },
  { valor: 'tudo', rotulo: 'Todo o histórico' },
];

function formataCurto(data) {
  const partes = String(data || '').split('-');
  return partes.length === 3 ? `${partes[2]}/${partes[1]}` : '';
}

export default {
  name: 'FiltroPeriodo',
  mixins: [dropdownPosicao],
  props: {
    value: {
      type: Object,
      default: () => ({ modo: 'hoje', dataInicio: '', dataFim: '' }),
    },
  },
  data() {
    return {
      aberto: false,
      atalhos: ATALHOS,
      pendente: { modo: 'hoje', dataInicio: '', dataFim: '' },
    };
  },
  computed: {
    resumo() {
      return this.descreve(this.value);
    },
    resumoPendente() {
      return this.descreve(this.pendente);
    },
    temPendencia() {
      return this.chave(this.pendente) !== this.chave(this.value);
    },
    // No modo custom é preciso ao menos uma das datas.
    podeAplicar() {
      if (this.pendente.modo !== 'custom') {
        return true;
      }

      return !!(this.pendente.dataInicio || this.pendente.dataFim);
    },
    avisoRange() {
      const { dataInicio, dataFim } = this.pendente;

      if (this.pendente.modo === 'custom' && !dataInicio && !dataFim) {
        return 'Informe pelo menos uma das datas.';
      }

      if (dataInicio && dataFim && dataInicio > dataFim) {
        return 'A data final é anterior à inicial — as duas serão invertidas.';
      }

      return '';
    },
  },
  methods: {
    chave(estado) {
      return [estado.modo, estado.dataInicio || '', estado.dataFim || ''].join('|');
    },
    descreve(estado) {
      if (estado.modo === 'custom') {
        const de = formataCurto(estado.dataInicio);
        const ate = formataCurto(estado.dataFim);

        if (de && ate) {
          return `${de} – ${ate}`;
        }
        if (de) {
          return `desde ${de}`;
        }
        if (ate) {
          return `até ${ate}`;
        }

        return 'Intervalo';
      }

      const atalho = ATALHOS.find((item) => item.valor === estado.modo);
      return atalho ? atalho.rotulo : 'Período';
    },
    // Escolher um atalho abandona o range específico.
    onAtalho() {
      this.pendente.dataInicio = '';
      this.pendente.dataFim = '';
    },
    // Digitar/limpar datas alterna entre custom e o atalho padrão.
    onData() {
      const preenchido = !!(this.pendente.dataInicio || this.pendente.dataFim);
      this.pendente.modo = preenchido ? 'custom' : 'hoje';
    },
    abrir() {
      this.pendente = { ...this.value };
      this.aberto = true;

      this.$nextTick(() => {
        // Painel já renderizado: agora dá para medir o espaço livre.
        this.observarPosicao();
        document.addEventListener('click', this.onClickFora, true);
        document.addEventListener('keydown', this.onEsc, true);
      });
    },
    fechar() {
      this.aberto = false;
      this.pararObservarPosicao();
      document.removeEventListener('click', this.onClickFora, true);
      document.removeEventListener('keydown', this.onEsc, true);
    },
    toggle() {
      if (this.aberto) {
        this.confirmar();
        return;
      }

      this.abrir();
    },
    onClickFora(evento) {
      const raiz = this.$refs.raiz;

      if (raiz && !raiz.contains(evento.target)) {
        this.confirmar();
      }
    },
    onEsc(evento) {
      if (evento.key === 'Escape') {
        this.fechar();
      }
    },
    confirmar() {
      // Pendência incompleta (custom sem data) é descartada em vez de aplicada.
      const aplicar = this.temPendencia && this.podeAplicar;
      const estado = { ...this.pendente };

      this.fechar();

      if (aplicar) {
        this.$emit('input', estado);
      }
    },
    aplicar() {
      this.confirmar();
    },
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onClickFora, true);
    document.removeEventListener('keydown', this.onEsc, true);
  },
};
</script>

<style scoped>
.fp {
  position: relative;
}

.fp__gatilho {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 13px;
  font-size: 12px;
  font-weight: 600;
  color: #2c3441;
  background: #fff;
  border: 1px solid #d4d9df;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
}

.fp__gatilho:hover {
  background: #f7f9fb;
}

.fp__gatilho--filtrando {
  border-color: #2b3556;
  color: #2b3556;
}

.fp__rotulo {
  color: #8a929c;
  font-weight: 400;
}

.fp__chevron {
  transition: transform 0.15s ease;
  opacity: 0.6;
}

.fp__chevron--aberto {
  transform: rotate(180deg);
}

.fp__painel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 41;
  width: 260px;
  max-width: 90vw;
  background: #fff;
  border: 1px solid #d9dee4;
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(16, 24, 40, 0.18);
}

.fp__acoes {
  padding: 8px 12px;
  border-bottom: 1px solid #eef1f4;
}

.fp__contagem {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8a929c;
}

.fp__lista {
  margin: 0;
  padding: 6px;
  list-style: none;
}

.fp__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin: 0;
  padding: 7px 8px;
  border-radius: 7px;
  cursor: pointer;
}

.fp__item:hover {
  background: #f7f9fb;
}

.fp__item--marcado {
  background: #f2f7fc;
}

.fp__item input {
  flex-shrink: 0;
  cursor: pointer;
}

.fp__nome {
  flex: 1;
  min-width: 0;
  font-size: 12.5px;
  font-weight: 600;
  color: #2c3441;
}

/* Range específico */
.fp__range {
  padding: 10px 12px;
  border-top: 1px solid #eef1f4;
  background: #fafbfc;
}

.fp__range-titulo {
  margin-bottom: 7px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8a929c;
}

.fp__campos {
  display: flex;
  gap: 8px;
}

.fp__campo {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
  margin: 0;
}

.fp__campo span {
  font-size: 10px;
  font-weight: 600;
  color: #8a929c;
}

.fp__campo input {
  width: 100%;
  padding: 5px 7px;
  font-size: 12px;
  color: #2c3441;
  background: #fff;
  border: 1px solid #d4d9df;
  border-radius: 6px;
}

.fp__campo input:focus {
  outline: none;
  border-color: #3d7fc4;
}

.fp__aviso {
  margin-top: 6px;
  font-size: 10.5px;
  line-height: 1.35;
  color: #b5651d;
}

.fp__rodape {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 12px;
  border-top: 1px solid #eef1f4;
  background: #f7f9fb;
  border-radius: 0 0 10px 10px;
}

.fp__pendente {
  font-size: 11px;
  color: #9aa2ac;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
